import { useTranslation } from 'react-i18next'
import {
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline'
import { useUserDocuments } from '../../hooks/useDocuments'
import { DocumentType } from '../../types'

type VerificationLevel = 'unverified' | 'pending' | 'verified'

const REQUIRED_TYPES: DocumentType[] = [
  DocumentType.IC_PASSPORT,
  DocumentType.PROOF_OF_ADDRESS,
  DocumentType.BIRTH_CERTIFICATE,
]

function useVerificationLevel(isVerified: boolean): VerificationLevel {
  const { data: documents = [] } = useUserDocuments()
  if (isVerified) return 'verified'
  const hasRelevant = documents.some((d) => REQUIRED_TYPES.includes(d.type))
  return hasRelevant ? 'pending' : 'unverified'
}

export default function VerificationStatusBanner({
  isVerified,
  onGoToDocs,
}: {
  isVerified: boolean
  onGoToDocs: () => void
}) {
  const { t } = useTranslation()
  const level = useVerificationLevel(isVerified)

  if (level === 'verified') {
    return (
      <div className="mx-6 mt-4 flex items-center gap-3 rounded-md border border-green-200 bg-green-50 px-4 py-3">
        <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
        <p className="text-sm font-medium text-green-800">
          {t('verification.verifiedTitle')}
        </p>
      </div>
    )
  }

  if (level === 'pending') {
    return (
      <div className="mx-6 mt-4 flex items-start gap-3 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3">
        <ClockIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500" />
        <div>
          <p className="text-sm font-semibold text-yellow-900">
            {t('verification.pendingTitle')}
          </p>
          <p className="mt-0.5 text-sm text-yellow-800">
            {t('verification.pendingDesc')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-6 mt-4 flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
        <div>
          <p className="text-sm font-semibold text-amber-900">
            {t('verification.unverifiedTitle')}
          </p>
          <p className="mt-0.5 text-sm text-amber-800">
            {t('verification.unverifiedDesc')}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onGoToDocs}
        className="inline-flex flex-shrink-0 items-center gap-1.5 self-start rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 sm:self-auto"
      >
        {t('verification.goToDocs')}
        <ArrowRightIcon className="h-4 w-4" />
      </button>
    </div>
  )
}
